import { AppType, ApiResponse, StrugglingWord, VerbTenseStat } from '@/types';
import { SessionDataDTO, WordProgressDTO } from '@/features/Vocab/vocab.ts';
import { coreApiClient } from './core';

/** A word offered in "skip known words". */
export interface VocabSortWord {
    id: number;
    text_el: string;
}

/** A word on the My words page. docs/hallouminati/question-words.md §My words */
export interface MyWord {
    id: number;
    text_el: string;
    translation: string;
    /** An exam-deck category (the topic chips), or null. */
    category: string | null;
    status: 'new' | 'learning' | 'learned' | 'known';
    /** The ladder rung, 0–3, while learning. */
    step: number | null;
    /** «Учить» serves it now. */
    due: boolean;
    next_review_at: string | null;
    /** Visible exam questions it is printed in. */
    questions: number;
}

/** The vocab tab's list pages besides My words. docs/hallouminati/vocab-trainer.md §Word lists */
export type VocabListId = 'mistakes' | 'learned' | 'struggling' | 'topic';

/** An exam question a word is printed in, split around the printed word. */
export interface MyWordQuestion {
    id: number;
    before: string;
    marked: string;
    after: string;
}

/** A row of GET /api/vocab/words. */
export interface WordListWord {
    id: number;
    text_el: string;
    text_en?: string;
    text_ru?: string;
    progress?: WordProgressDTO | null;
}

/** GET /api/vocab/words: up to 50 words per state, or one page of the `state` filter. */
export interface WordListData {
    mastered_words: WordListWord[];
    learning_words: WordListWord[];
    new_words: WordListWord[];
    stats: {
        total_count?: number;
        mastered_count?: number;
        learning_count?: number;
        new_count?: number;
        learned_count?: number;
        /** Officials category only. */
        mastery_pct?: number;
        in_progress_pct?: number;
        progress_pct?: number;
    };
    /** Only when `state` was sent. */
    pagination?: { page: number; per_page: number; total: number; total_pages: number };
    /** Only for wordClass=verb. */
    verb_tense_stats?: Record<string, VerbTenseStat>;
}

type QueryParams = Record<string, string | number>;

export const vocabApi = {
    async getWords(
        appType: AppType,
        wordClass?: string | null,
        topic?: string | null,
        level?: number | string | null,
        formLabels?: string | null,
        category?: string | null,
        state?: string | null,
        page?: number | null,
    ): Promise<ApiResponse<WordListData>> {
        try {
            const params: QueryParams = { app_type: appType };
            if (wordClass) {
                params.word_class = wordClass;
            }
            if (topic) params.topic = topic;
            if (level !== null && level !== undefined && level !== '') params.level = level;
            if (formLabels) params.formLabels = formLabels;
            if (category) params.category = category;
            // `state` opts into server-side pagination (50 per page) for the active
            // filter; `page` is 1-based. Both are omitted by non-paginated callers.
            if (state) params.state = state;
            if (page && page > 1) params.page = page;
            const response = await coreApiClient.axios.get<ApiResponse<WordListData>>(
                '/api/vocab/words',
                { params }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to load words'
            };
        }
    },

    async getStrugglingWords(appType: AppType): Promise<ApiResponse<{ struggling_words: StrugglingWord[] }>> {
        try {
            const response = await coreApiClient.axios.get<ApiResponse<{ struggling_words: StrugglingWord[] }>>(
                '/api/vocab/struggling-words',
                { params: { app_type: appType } }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to load struggling words'
            };
        }
    },

    async getSRSSession(
        appType: string,
        wordClass?: string | null,
        mode?: string | null,
        topic?: string | null,
        formLabels?: string | null,
        level?: number | null,
        category?: string | null,
        pictureWords?: boolean | null,
        pictureSlug?: string | null,
        dialogueWords?: boolean | null,
    ): Promise<ApiResponse<SessionDataDTO>> {
        try {
            const params: QueryParams = {};
            if (wordClass) params.wordClass = wordClass;
            if (level !== null && level !== undefined) params.level = level;
            if (mode) params.mode = mode;
            if (topic) params.topic = topic;
            if (formLabels) params.formLabels = formLabels;
            if (category) params.category = category;
            if (pictureWords) params.pictureWords = 1;
            if (pictureSlug) params.pictureSlug = pictureSlug;
            if (dialogueWords) params.dialogueWords = 1;
            // This bundle renders vocab_first_check; without the flag the API
            // serves flashcards (docs/hallouminati/vocab-first-check-and-placement.md §4).
            params.firstCheck = 1;
            // …and vocab_ladder in place of the self-graded flashcard
            // (docs/hallouminati/vocab-trainer.md §The ladder).
            params.ladder = 1;

            const response = await coreApiClient.axios.get<ApiResponse<SessionDataDTO>>(
                `/api/vocab/${appType}/srs_session`,
                { params }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to load session'
            };
        }
    },

    async getDemoSession(): Promise<ApiResponse<SessionDataDTO>> {
        try {
            const response = await coreApiClient.axios.get<ApiResponse<SessionDataDTO>>(
                '/api/greekbrain/practice/demo'
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to load demo session'
            };
        }
    },

    async markWordLearned(wordId: number): Promise<ApiResponse<void>> {
        try {
            const response = await coreApiClient.axios.post<ApiResponse<void>>(
                '/api/vocab/mark-learned',
                { word_id: wordId }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to mark word'
            };
        }
    },

    /**
     * "My words" toggle from the quiz «Слова» sheet. The server stores the
     * lemma and returns its id. docs/hallouminati/question-words.md §My words
     */
    async setMyWord(wordId: number, on: boolean): Promise<{ success: boolean; word_id?: number; my_words_count?: number; error?: string }> {
        try {
            const response = on
                ? await coreApiClient.axios.post('/api/vocab/my-words', { word_id: wordId })
                : await coreApiClient.axios.delete(`/api/vocab/my-words/${wordId}`);
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to update My words'
            };
        }
    },

    /**
     * "Skip known words": the next exam-minimum words the learner has never
     * seen, and applying a sorted batch. docs/hallouminati/vocab-trainer.md
     */
    async getVocabSortBatch(): Promise<{ success: boolean; words?: VocabSortWord[]; remaining?: number; error?: string }> {
        try {
            const response = await coreApiClient.axios.get('/api/hallouminati/vocab/sort');
            return response.data;
        } catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Failed to load words' };
        }
    },

    async applyVocabSort(knownIds: number[], unknownIds: number[]): Promise<{ success: boolean; known?: number; unknown?: number; error?: string }> {
        try {
            const response = await coreApiClient.axios.post('/api/hallouminati/vocab/sort', { known_ids: knownIds, unknown_ids: unknownIds });
            return response.data;
        } catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Failed to save' };
        }
    },

    /** The My words page: the whole list. docs/hallouminati/question-words.md §My words */
    async getMyWords(): Promise<{ success: boolean; words?: MyWord[]; more?: number; error?: string }> {
        try {
            const response = await coreApiClient.axios.get('/api/hallouminati/vocab/my-words');
            return response.data;
        } catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Failed to load My words' };
        }
    },

    /** A vocab-tab list page's words, as My-words rows. docs/hallouminati/vocab-trainer.md §Word lists */
    async getVocabList(list: VocabListId, category?: string): Promise<{ success: boolean; words?: MyWord[]; more?: number; error?: string }> {
        try {
            const response = await coreApiClient.axios.get(`/api/hallouminati/vocab/lists/${list}`, {
                params: category ? { category } : undefined,
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Failed to load the list' };
        }
    },

    async getMyWordQuestions(wordId: number): Promise<{ success: boolean; total?: number; questions?: MyWordQuestion[]; error?: string }> {
        try {
            const response = await coreApiClient.axios.get(`/api/hallouminati/vocab/my-words/${wordId}/questions`);
            return response.data;
        } catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Failed to load the questions' };
        }
    },

    async markWordAsUnknown(wordId: number): Promise<ApiResponse<void>> {
        try {
            const response = await coreApiClient.axios.post<ApiResponse<void>>(
                '/api/vocab/mark-unknown',
                { word_id: wordId }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to mark word'
            };
        }
    }
};
